import React from 'react'
import { Link, useParams } from 'react-router-dom'
import { MODEL_TYPE } from '@trustero/trustero-api-web/lib/common/model_pb'
import {
  ActionGridColumn,
  GridColumn,
  GridRow,
} from 'src/components/Reusable/Grid'
import { DepartmentChip } from 'src/pages/Controls/DepartmentChip' // TODO: move into global helper
import { useHasRequiredPermissions } from 'src/app/AppAuth/AppAuth.hooks'
import { PERMISSIONS } from 'src/config/roleConfig'
import { StatusDropdown } from '../risks.components'
import { OwnerAssignmentButton } from '../../../components/Reusable/Buttons'
import { generatePermalink } from '../../../components/PageLayout/Permalink'
import { RiskRowMenu } from '../menus'
import { RiskName } from '../risks.styles'
import { RiskFields } from '../risks.constants'
import {
  CalculatedRiskMathChip,
  CalculatedActualResidualRisk,
} from '../risks.components'
import { useRisk, useRiskComputedValues } from '../risks.hooks'

const RiskGridRow = ({ riskId }: { riskId: string }): JSX.Element => {
  const params = useParams()
  const risk = useRisk(riskId)
  const { getCategoryName } = useRiskComputedValues()
  const hasMenuPermissions = useHasRequiredPermissions([
    PERMISSIONS.READ,
    PERMISSIONS.EDIT,
  ])

  if (!risk) {
    return <></>
  }

  // Risk data
  const id = risk.getId()
  const name = risk.getName()
  const customId = risk.getCustomId()
  const controlIds = risk.getControlIds()
  const controlCount = controlIds.length ? controlIds.split(',').length : 0
  const departmentId = risk.getDepartmentId()
  const ownerEmail = risk.getOwnerEmail()
  const impact = risk.getAdverseImpact()
  const likelihood = risk.getLikelihood()
  const targetAdverseImpact = risk.getTargetAdverseImpact()
  const targetLikelihood = risk.getTargetLikelihood()

  const categoryName = getCategoryName(risk)
  const permalink = generatePermalink({
    pageContext: params.pageContext as string,
    modelType: MODEL_TYPE.RISK,
    modelId: customId,
    isInternalLink: true,
    includeSearchParams: true,
  })

  return (
    <GridRow id={`risks-index-row-${id}`} as={Link} to={permalink}>
      <GridColumn>
        <DepartmentChip departmentId={departmentId} />
      </GridColumn>
      <GridColumn>{customId}</GridColumn>
      <GridColumn>
        <RiskName>{name}</RiskName>
      </GridColumn>
      <GridColumn>{categoryName}</GridColumn>
      <GridColumn>{controlCount}</GridColumn>
      <GridColumn>
        <CalculatedRiskMathChip
          riskId={id}
          impact={impact}
          likelihood={likelihood}
          field={RiskFields.inherentRisk}
        />
      </GridColumn>
      <GridColumn>
        <CalculatedRiskMathChip
          riskId={id}
          impact={targetAdverseImpact}
          likelihood={targetLikelihood}
          field={RiskFields.riskAfterTreatment}
        />
      </GridColumn>
      <GridColumn>
        <CalculatedActualResidualRisk
          risk={risk}
          field={RiskFields.actualResidualRisk}
        />
      </GridColumn>
      <GridColumn>
        <StatusDropdown risk={risk} />
      </GridColumn>
      <GridColumn>
        <OwnerAssignmentButton
          model={risk}
          modelType={MODEL_TYPE.RISK}
          modelId={id}
          email={ownerEmail}
        />
      </GridColumn>
      <ActionGridColumn>
        {hasMenuPermissions && <RiskRowMenu risk={risk} />}
      </ActionGridColumn>
    </GridRow>
  )
}

export const RiskGridRows = ({
  riskIds,
}: {
  riskIds: string[]
}): JSX.Element => {
  return (
    <>
      {riskIds.map((riskId: string) => (
        <RiskGridRow key={riskId} riskId={riskId} />
      ))}
    </>
  )
}
